import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import client from '../../api/client';

/**
 * Renders an authenticated receipt preview (image or PDF).
 * Uses apiClient with JWT to fetch the file as a blob,
 * then creates a local blob: URL for the <img> or <iframe> tag.
 * Cleans up the blob URL on unmount.
 */
export default function AuthedReceiptPreview({ src, fileType, alt, style, imgStyle, iframeStyle }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) { setLoading(false); return; }
    let revoked = false;
    client.get(src, { responseType: 'blob' })
      .then(res => {
        if (revoked) return;
        const url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => { if (!revoked) setError(true); })
      .finally(() => { if (!revoked) setLoading(false); });
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <Loader2 size={16} className="spin" style={{ color: '#94a3b8' }} />
      </div>
    );
  }

  if (error || !blobUrl) return null;

  const isImage = fileType?.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  if (isImage) {
    return <img src={blobUrl} alt={alt || 'recibo'} style={imgStyle || style} />;
  }

  if (isPdf) {
    return <iframe src={blobUrl} title={alt || 'recibo'} style={iframeStyle || style} />;
  }

  return null;
}
